import { StatusPill, type StatusTone } from "@/components/shared/status-badge";
import type { NosLinkCheck, NosLinkRow, NosLinkStatus } from "@/hooks/use-nos-link-check";

/**
 * The NOS column shared by the two config pages that link to NOS (employee
 * profiles, role classifications). It answers one question — does this row line
 * up with NOS? — and shows the actual NOS id when it does, so the two systems
 * can be compared without leaving the page.
 *
 * The id is rendered in full rather than truncated: the whole point is being
 * able to read it against NOS, and a shortened guid can't be checked.
 */
const TONE: Record<NosLinkStatus, StatusTone> = {
  matched: "success",
  // Pushes correctly (the guid is the key) but the two systems read differently
  // to their users, which is worth surfacing rather than calling it a match.
  name_differs: "warning",
  missing_in_nos: "danger",
  not_linked: "warning",
};

const LABEL: Record<NosLinkStatus, string> = {
  matched: "Matched",
  name_differs: "Name differs",
  missing_in_nos: "Not found in NOS",
  not_linked: "Not linked",
};

export function NosLinkCell({
  check,
  row,
  loading,
}: {
  check: NosLinkCheck | undefined;
  row: NosLinkRow | undefined;
  loading: boolean;
}) {
  if (loading) {
    return <span className="text-xs text-muted-foreground">Checking…</span>;
  }
  // Not connected / unreachable: the page-level note explains it once, so each
  // row stays quiet rather than repeating an error a dozen times.
  if (!check?.configured || !check.reachable || !row) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  return (
    <div className="space-y-1">
      <StatusPill tone={TONE[row.status]} label={LABEL[row.status]} />
      {row.nosId ? (
        <code className="block text-2xs leading-tight text-muted-foreground">{row.nosId}</code>
      ) : null}
      {row.status === "name_differs" && row.nosName ? (
        <div className="text-2xs leading-tight text-muted-foreground">
          NOS calls it "{row.nosName}"
        </div>
      ) : null}
    </div>
  );
}

/**
 * The one-line explanation shown above the table. Renders nothing when the
 * check succeeded and every row matched — silence is the right output for
 * "everything is fine", and a permanent green banner trains people to ignore it.
 */
export function NosLinkNote({
  check,
  loading,
  /** What the rows are, for the "N of M" sentence — e.g. "profiles". */
  noun,
}: {
  check: NosLinkCheck | undefined;
  loading: boolean;
  noun: string;
}) {
  if (loading) {
    return <p className="text-xs text-muted-foreground">Checking IDs against NOS…</p>;
  }
  if (!check) return null;

  if (!check.configured) {
    return (
      <p className="text-xs text-muted-foreground">
        NOS isn't connected in this environment, so the IDs can't be checked here. They're verified
        wherever the NOS connection is configured.
      </p>
    );
  }
  if (!check.reachable) {
    return (
      <p className="text-xs text-warning-foreground">
        Couldn't reach NOS to check the IDs{check.error ? `: ${check.error}` : ""}. The list above
        is unaffected — try again shortly.
      </p>
    );
  }

  const matched = check.rows.filter((r) => r.status === "matched").length;
  const problems = check.rows.length - matched;
  const missing = check.unmatchedInNos;

  if (problems === 0 && missing.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        All {check.rows.length} {noun} match NOS.
      </p>
    );
  }

  return (
    <div className="space-y-1 text-xs">
      <p className="text-warning-foreground">
        {matched} of {check.rows.length} {noun} match NOS. The NOS column says what's wrong with
        each of the others.
      </p>
      {missing.length > 0 && (
        <p className="text-muted-foreground">
          NOS also has {missing.length === 1 ? "one" : missing.length} Enable doesn't:{" "}
          {missing.map((m) => m.name).join(", ")}.
        </p>
      )}
    </div>
  );
}
